import React, { useState, useEffect } from 'react';
import {
    ClipboardList, Calendar, CheckCircle, Clock, 
    ChevronRight, AlertCircle, FileText, Paperclip
} from 'lucide-react';
import api from '../utils/api';
import { useSurvey } from '../context/SurveyContext';
import toast from 'react-hot-toast';

const EmployeeSurveysPage = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('pending'); // pending, completed
    const [viewingResponses, setViewingResponses] = useState(null);
    const { startSurvey } = useSurvey();

    const fetchSurveys = async () => {
        console.log('[Survey Page] fetchSurveys called');
        setLoading(true);
        try {
            const response = await api.get('/surveys/employee/my-surveys');
            setSurveys(response.data.surveys || []);
        } catch (error) {
            console.error('Error fetching surveys:', error);
            toast.error('Failed to load surveys');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('[Survey Page] Component mounted');
        fetchSurveys();

        // Listen for survey completion events
        const handleSurveyCompleted = () => {
            console.log('[Survey Page] Survey completed, refreshing list');
            fetchSurveys();
        };

        window.addEventListener('surveyCompleted', handleSurveyCompleted);
        return () => {
            console.log('[Survey Page] Component unmounting');
            window.removeEventListener('surveyCompleted', handleSurveyCompleted);
        };
    }, []);

    const handleStartSurvey = (instanceId) => {
        startSurvey(instanceId);
    };

    const handleViewResponses = async (instanceId) => {
        try {
            const response = await api.get(`/surveys/employee/instance/${instanceId}/responses`);
            setViewingResponses({
                instanceId,
                responses: response.data.responses || []
            });
        } catch (error) {
            console.error('Error fetching responses:', error);
            toast.error('Failed to load your responses');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'No due date';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const pendingSurveys = surveys.filter(s => 
        s.status === 'pending' || s.status === 'in_progress'
    );
    const completedSurveys = surveys.filter(s => s.status === 'completed');

    const renderSurveyCard = (survey) => {
        const isPending = survey.status === 'pending' || survey.status === 'in_progress';
        
        return (
            <div
                key={survey.instanceId}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <ClipboardList className="w-5 h-5 text-primary" />
                            <h3 className="text-lg font-semibold text-gray-800">
                                {survey.title}
                            </h3>
                            {survey.isComplianceRequired && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                    Required
                                </span>
                            )}
                        </div>
                        
                        {survey.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                {survey.description}
                            </p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            {isPending && (
                                <div className="flex items-center gap-1.5">
                                    <Calendar className="w-4 h-4" />
                                    <span>Assigned: {formatDate(survey.deployedAt)}</span>
                                </div>
                            )}
                            {survey.status === 'completed' && (
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>Completed: {formatDate(survey.completedAt)}</span>
                                </div>
                            )}
                            {survey.status === 'in_progress' && (
                                <div className="flex items-center gap-1.5">
                                    <Clock className="w-4 h-4 text-amber-500" />
                                    <span className="text-amber-600 font-medium">In Progress</span>
                                </div>
                            )}
                        </div>

                        {survey.attachments && survey.attachments.length > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                                <Paperclip className="w-3.5 h-3.5" />
                                <span>{survey.attachments.length} attachment(s)</span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        {isPending ? (
                            <button
                                onClick={() => handleStartSurvey(survey.instanceId)}
                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                            >
                                {survey.status === 'in_progress' ? 'Continue' : 'Start Survey'}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                onClick={() => handleViewResponses(survey.instanceId)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                            >
                                <FileText className="w-4 h-4" />
                                View Responses
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading surveys...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-4 lg:px-6 py-4 sm:py-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Surveys</h1>
                    <p className="text-sm text-gray-500 mt-1">Complete surveys to share your feedback</p>
                </div>
            </div>

            <div className="px-4 lg:px-6 py-4 sm:py-6">
                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                <Clock className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{pendingSurveys.length}</p>
                                <p className="text-sm text-gray-500">Pending Surveys</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">{completedSurveys.length}</p>
                                <p className="text-sm text-gray-500">Completed Surveys</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-sm mb-6">
                    <div className="border-b border-gray-200">
                        <div className="flex">
                            <button
                                onClick={() => setSelectedTab('pending')}
                                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                                    selectedTab === 'pending'
                                        ? 'text-primary'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Pending ({pendingSurveys.length})
                                {selectedTab === 'pending' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                                )}
                            </button>
                            <button
                                onClick={() => setSelectedTab('completed')}
                                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                                    selectedTab === 'completed'
                                        ? 'text-primary'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Completed ({completedSurveys.length})
                                {selectedTab === 'completed' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Survey List */}
                <div className="space-y-4">
                    {selectedTab === 'pending' && (
                        <>
                            {pendingSurveys.length === 0 ? (
                                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                                    <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                        No Pending Surveys
                                    </h3>
                                    <p className="text-gray-500">
                                        You're all caught up! Check back later for new surveys.
                                    </p>
                                </div>
                            ) : (
                                pendingSurveys.map(renderSurveyCard)
                            )}
                        </>
                    )}

                    {selectedTab === 'completed' && (
                        <>
                            {completedSurveys.length === 0 ? (
                                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                        No Completed Surveys
                                    </h3>
                                    <p className="text-gray-500">
                                        Complete your first survey to see it here.
                                    </p>
                                </div>
                            ) : (
                                completedSurveys.map(renderSurveyCard)
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Response Viewer Modal */}
            {viewingResponses && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">Your Responses</h2>
                            <button
                                onClick={() => setViewingResponses(null)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {viewingResponses.responses.length === 0 ? (
                                <div className="text-center py-12">
                                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-500">No responses found</p>
                                </div>
                            ) : (
                                viewingResponses.responses.map((response, idx) => (
                                    <div key={response.id} className="border-b border-gray-100 pb-6 last:border-0">
                                        <div className="flex items-start gap-3 mb-2">
                                            <span className="flex-shrink-0 w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-semibold">
                                                {idx + 1}
                                            </span>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-800 mb-2">
                                                    {response.question_text}
                                                </p>
                                                {response.skipped ? (
                                                    <p className="text-sm text-gray-400 italic">Skipped</p>
                                                ) : (
                                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                        <p className="text-gray-700">
                                                            {response.response_text || response.response_value || 'No response'}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeSurveysPage;
